// Shared project-pack PDF export: rasterised cover + datasheets, merged archive
// PDFs/images, with highlighter marks + TYPE overlays drawn onto archive pages.
import { exportPagesToPdf, exportPagesToPdfBytes } from './pdfExport';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { api } from './api';

export function decorateArchivePage(page, it, pageIdx, font) {
  const { width: pw, height: ph } = page.getSize();
  const rects = ((it.highlights || {})[String(pageIdx + 1)]) || [];
  rects.forEach(r => page.drawRectangle({
    x: (r.x / 100) * pw, y: ph - ((r.y + r.h) / 100) * ph,
    width: (r.w / 100) * pw, height: (r.h / 100) * ph,
    color: rgb(1, 0.9, 0.2), opacity: 0.4,
  }));
  // Box sizes proportional to the 794px preview page so PDF matches what the studio shows.
  const bw = pw * (112 / 794), bh = pw * (42 / 794);
  const drawTypeBox = (bx, by, txt) => {
    page.drawRectangle({ x: bx, y: by, width: bw, height: bh, color: rgb(1, 1, 1), opacity: 0.85, borderColor: rgb(0.75, 0.22, 0.17), borderWidth: 1.2 });
    const size = (txt.length > 9 ? 8 : 12) * (pw / 595.28);
    const tw = font.bold.widthOfTextAtSize(txt, size);
    page.drawText(txt, { x: bx + Math.max(3, (bw - tw) / 2), y: by + (bh - size) / 2 + 1, size, font: font.bold, color: rgb(0.75, 0.22, 0.17) });
  };
  const t = (it.typeOverlay || '').trim();
  if (t && pageIdx === 0) drawTypeBox(pw - pw * (26 / 794) - bw, ph - pw * (12 / 794) - bh, t);
  (((it.typeBoxes || {})[String(pageIdx + 1)]) || []).forEach(b => {
    const txt = (b.text || '').trim();
    if (txt) drawTypeBox((b.x / 100) * pw, ph - (b.y / 100) * ph - bh, txt);
  });
  const scale = pw / 794;
  (((it.textBoxes || {})[String(pageIdx + 1)]) || []).forEach(b => {
    const txt = (b.text || '').trim();
    if (!txt) return;
    const size = (b.size || 14) * scale;
    const xText = (b.x / 100) * pw + 5 * scale;
    const yText = ph - (b.y / 100) * ph - size - 3 * scale;
    if (b.hl) {
      const lines = txt.split('\n');
      const w = Math.max(...lines.map(l => font.regular.widthOfTextAtSize(l, size)));
      const h = size + (lines.length - 1) * size * 1.25 + 4 * scale;
      page.drawRectangle({
        x: xText - 2 * scale, y: yText - (lines.length - 1) * size * 1.25 - 2 * scale,
        width: w + 4 * scale, height: h, color: rgb(1, 0.88, 0.11), opacity: 0.48,
      });
    }
    page.drawText(txt, {
      x: xText, y: yText,
      size, font: font.regular, color: rgb(0.1, 0.12, 0.15), lineHeight: size * 1.25,
    });
  });
}

export async function exportProjectPack(rootEl, list, name, onWarn) {
  const fname = (name || 'project').replace(/[^a-zA-Z0-9_-]+/g, '_') + '.pdf';
  const hasArchive = list.some(it => it.type === 'archive');
  if (!hasArchive) {
    await exportPagesToPdf(rootEl, fname, { title: name || 'Project' });
    return;
  }
  // Page counts come from the rendered DOM: US kit sheets run 5-7 pages, AU 1-2.
  const wrappers = Array.from(rootEl.querySelectorAll('[data-export-page]'));
  const sheetCounts = wrappers.slice(1).map(w => w.querySelectorAll('.ds-page').length || 1);
  const firstPage = rootEl.querySelector('.ds-page');
  const letter = firstPage
    ? Math.abs((firstPage.offsetWidth / firstPage.offsetHeight) - (8.5 / 11)) < 0.01 : false;
  const [imgW, imgH] = letter ? [612, 792] : [595.28, 841.89];
  const bytes = await exportPagesToPdfBytes(rootEl, { title: name || 'Project' });
  const base = await PDFDocument.load(bytes);
  const out = await PDFDocument.create();
  const font = {
    bold: await out.embedFont(StandardFonts.HelveticaBold),
    regular: await out.embedFont(StandardFonts.Helvetica),
  };
  const [coverPage] = await out.copyPages(base, [0]);
  out.addPage(coverPage);
  let cursor = 1;
  let sheetIdx = 0;
  for (const it of list) {
    if (it.type === 'archive') {
      try {
        const url = await api.getLegacyObjectUrl(it.legacyId);
        const buf = await (await fetch(url)).arrayBuffer();
        const ct = (it.contentType || '').toLowerCase();
        if (ct.includes('pdf') || /\.pdf$/i.test(it.name || '')) {
          const src = await PDFDocument.load(buf, { ignoreEncryption: true });
          const indices = it.page ? [Math.min(it.page, src.getPageCount()) - 1] : src.getPageIndices();
          (await out.copyPages(src, indices)).forEach((p, pi) => { out.addPage(p); decorateArchivePage(p, it, pi, font); });
        } else {
          const img = ct.includes('png') ? await out.embedPng(buf) : await out.embedJpg(buf);
          const page = out.addPage([imgW, imgH]);
          const sc = Math.min((imgW - 90) / img.width, (imgH - 90) / img.height, 1.5);
          const w = img.width * sc, h = img.height * sc;
          page.drawImage(img, { x: (imgW - w) / 2, y: (imgH - h) / 2, width: w, height: h });
          decorateArchivePage(page, it, 0, font);
        }
      } catch (err) {
        console.error('Archive merge failed for', it.name, err);
        if (onWarn) onWarn('Could not include "' + it.name + '" — skipped');
      }
    } else {
      const n = sheetCounts[sheetIdx] || 1;
      sheetIdx += 1;
      const idx = []; for (let k = 0; k < n; k++) idx.push(cursor + k);
      (await out.copyPages(base, idx)).forEach(p => out.addPage(p));
      cursor += n;
    }
  }
  const outBytes = await out.save();
  const blob = new Blob([outBytes], { type: 'application/pdf' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = fname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
}
